import { useEffect, useMemo, useState } from "react";
import { RotateCcw, Check, X as XIcon, Eye, ChevronDown } from "lucide-react";
import { Button } from "../../components/UI/Button";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import Modal from "../../components/UI/Modal";
import { CompanyOrderRepositoryImpl } from "../../../infrastructure/repositories/company/company-order-repository-impl";
import { CompanyOrderUsecase } from "../../../domain/usecases/company/company-order-usecase";
import { useParams } from "react-router-dom";

export type OrderStatus =
  | "pending_payment"
  | "accepted"
  | "resubmission"
  | "production"
  | "production_complete"
  | "shipping"
  | "cancelled";

export type Resubmission = {
  id: number;
  message: string;
  orderId: number;
  requestedAt: string;
  requestedBy: {
    id: number;
    name: string;
    email: string;
    mobile: string | null;
  };
};

export type Order = {
  id: string;
  orderNumber: string;
  customerName: string;
  customerEmail: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalAmount: number;
  status: OrderStatus;
  orderDate: string;
  updateDate: string;
  dueDate: string;
  memo?: string;
  optionsText?: string;
  files?: string;
  resubmissionMemo?: string;
  resubmissions?: Resubmission[];
};

const apiStatusToUiStatus = (status: string): OrderStatus => {
  switch (status) {
    case "BEFORE_PAYMENT":
      return "pending_payment";
    case "RECEIVED":
      return "accepted";
    case "RE_RECEIVED":
      return "resubmission";
    case "IN_PRODUCTION":
      return "production";
    case "PRODUCTION_COMPLETED":
      return "production_complete";
    case "SHIPPED":
      return "shipping";
    case "REFUNDED":
      return "cancelled"; // 프론트의 취소/환불 합침 상태로 임시 매핑
    default:
      return "accepted"; // 기본값(안전)
  }
};

// 백엔드 요약 리스트 → 프론트 Order 타입 매핑 (부족한 값은 기본값 세팅)
const mapApiOrderToView = (o: any): Order => {
  const resubmissions: Resubmission[] = (o.resubmissions ?? []) as Resubmission[];
  const lastMessage =
    resubmissions.length > 0 ? resubmissions[resubmissions.length - 1].message : o.resubmissionMemo;

  return {
    id: String(o.id),
    orderNumber: String(o.orderNumber ?? o.id),
    customerName: o.purchaser?.name ?? "",
    customerEmail: o.purchaser?.email ?? "",
    productName: o.name, // 서버 요약에 상품명이 없다면 백엔드 보완 전까지 주문명으로 대체
    quantity: o.productCount ?? 0,
    unitPrice: o.productPrice ?? 0,
    totalAmount: o.orderTotalAmount ?? 0,
    status: apiStatusToUiStatus(o.status),
    orderDate: (o.createdAt ?? "").toString().slice(0, 10),
    updateDate: (o.updatedAt ?? "").toString().slice(0, 10),
    dueDate: "", // 요약 응답에 없으므로 빈 값
    memo: o.memo,
    optionsText: "",
    files: o.orderSourceFile,
    resubmissionMemo: lastMessage,
    resubmissions,
  };
};

const ResubmissionManagement: React.FC = () => {
  const { companyId } = useParams();

  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [openedRowId, setOpenedRowId] = useState<string | null>(null);

  const repo = useMemo(() => new CompanyOrderRepositoryImpl(), []);
  const usecase = useMemo(() => new CompanyOrderUsecase(repo), [repo]);

  const [resubmissionOrders, setResubmissionOrders] = useState<Order[]>();

  const handleAcceptResubmission = (orderId: string) => {
    // setResubmissionOrders((orders) =>
    //   orders.filter((order) => order.id !== orderId),
    // );
    alert("재접수가 승인되었습니다. 제작 단계로 이동합니다.");
  };

  const handleRejectResubmission = (orderId: string) => {
    const reason = prompt("거부 사유를 입력해주세요:");
    if (reason) {
      // setResubmissionOrders((orders) =>
      //   orders.filter((order) => order.id !== orderId),
      // );
      alert("재접수가 거부되었습니다.");
    }
  };

  const handleViewDetail = (order: Order) => {
    setSelectedOrder(order);
    setIsDetailOpen(true);
  };

  useEffect(() => {
    (async () => {
      try {
        const cid = Number(companyId);
        if (!Number.isFinite(cid)) return;

        const result = await usecase.resubmissionList(cid); 
        console.log(result);
        const mapped = result.map(mapApiOrderToView);
        setResubmissionOrders(mapped);
      } catch (e: any) {
        console.error(e);
        alert("재접수 리스트를 불러오는데 실패하였습니다.");
      }

    })();
  }, [companyId]);

  return (
    <>
      {/* Order Detail Modal */}
      {isDetailOpen && selectedOrder && (
        <Modal setIsModalVisible={setIsDetailOpen} title="재접수 요청 상세">
          <>
            <div className="w-[70vw] max-w-[500px]"></div>

            <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded mt-[1.25rem] mb-[1.25rem]">
              <div>
                <p className="text-sm text-gray-600">주문번호</p>
                <p className="font-medium">{selectedOrder.orderNumber}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">주문자</p>
                <p className="font-medium">{selectedOrder.customerName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">상품명</p>
                <p className="font-medium">{selectedOrder.productName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">수량</p>
                <p className="font-medium">{selectedOrder.quantity}개</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">주문일</p>
                <p className="font-medium">{selectedOrder.orderDate}</p>
              </div>
            </div>

            <h3 className="text-start text-[16px] font-bold mb-3">
              재접수 요청 내역
            </h3>
            <div className="rounded-lg border border-orange-200 bg-orange-50/60 p-4">
              {selectedOrder.resubmissions && selectedOrder.resubmissions.length > 0 ? (
                <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                  {selectedOrder.resubmissions.map((r, index) => (
                    <div
                      key={r.id}
                      className="rounded-md border border-orange-100 bg-white/70 px-3 py-2 shadow-[0_1px_2px_rgba(15,23,42,0.06)]"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center rounded-full bg-orange-100 text-orange-700 text-[10px] px-2 py-0.5">
                            요청 {index + 1}
                          </span>
                          <span className="text-[11px] font-medium text-gray-800">
                            {r.requestedBy?.name ?? "요청자"}
                          </span>
                        </div>
                        <span className="text-[10px] text-gray-500">
                          {r.requestedAt
                            ? r.requestedAt.replace("T", " ").slice(0, 16)
                            : ""}
                        </span>
                      </div>
                      <p className="whitespace-pre-wrap break-words text-[12px] leading-relaxed text-gray-800">
                        {r.message}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-gray-600">
                  등록된 재접수 요청 메모가 없습니다.
                </div>
              )}
            </div>

            <div className="flex justify-between mt-5 pt-4 border-t">
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    handleAcceptResubmission(selectedOrder.id);
                    setIsDetailOpen(false);
                  }}
                  className="bg-[#4caf50] hover:bg-[#45a049] text-white flex items-center"
                >
                  <Check className="w-4 h-4 mr-2" />
                  재접수 승인
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    handleRejectResubmission(selectedOrder.id);
                    setIsDetailOpen(false);
                  }}
                  className="border-red-300 text-red-600 hover:bg-red-50 flex items-center"
                >
                  <XIcon className="w-4 h-4 mr-2" />
                  재접수 거부
                </Button>
              </div>
              <Button variant="outline" onClick={() => setIsDetailOpen(false)}>
                닫기
              </Button>
            </div>
          </>
        </Modal>
      )}

      <CompanyAdminLayout>
        <div className="p-5">
          <div className="mb-[28px] flex flex-col items-start gap-[0.5rem]">
            <h2 className="text-[1.375rem] font-semibold">재접수 관리</h2>
            <div className="flex items-center justify-between w-full">
              <span className="text-[1rem] font-normal">
                재접수가 요청된 주문을 확인하고 처리하세요. (
                {(resubmissionOrders || []).length}건)
              </span>
            </div>
          </div>

          <div className="text-[12px]">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="px-3 py-2 text-left font-medium">주문번호</th>
                  <th className="px-3 py-2 text-left font-medium">주문자</th>
                  <th className="px-3 py-2 text-left font-medium">상품명</th>
                  <th className="px-3 py-2 text-left font-medium">수량</th>
                  <th className="px-3 py-2 text-left font-medium">요청일</th>
                </tr>
              </thead>
              <tbody>
                {(resubmissionOrders || []).map((item) => (
                  <>
                    <tr
                      key={item.id}
                      className="border-b border-gray-100 bg-white hover:bg-gray-50 transition-colors text-left"
                    >
                      <td className="px-3 py-2 align-middle text-left">{item.orderNumber}</td>
                      <td className="px-3 py-2 align-middle text-left">
                        <div className="flex flex-col">
                          <div>{item.customerName}</div>
                          <div className="text-[#757575]">{item.customerEmail}</div>
                        </div>
                      </td>
                      <td className="px-3 py-2 align-middle text-left">
                        <div className="flex flex-col">
                          <div>{item.productName}</div>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 text-[11px] text-[#5c9cff] cursor-pointer mt-1 hover:underline"
                            onClick={() =>
                              setOpenedRowId((prev) =>
                                prev === item.id ? null : item.id
                              )
                            }
                          >
                            <span>
                              재접수 요청 {(item.resubmissions || []).length}건
                            </span>
                            <ChevronDown
                              className={`w-3 h-3 transition-transform duration-150 ${
                                openedRowId === item.id ? "rotate-180" : ""
                              }`}
                            />
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-2 align-middle text-left">{item.quantity}개</td>
                      <td className="px-3 py-2 align-middle text-left">{item.orderDate}</td>
                    </tr>

                    {openedRowId === item.id &&
                      (item.resubmissions || []).length > 0 && (
                        <tr
                          key={`${item.id}-resubmissions`}
                          className="border-b border-gray-100 bg-orange-50/40"
                        >
                          <td className="px-3 py-3" colSpan={5}>
                            <div className="rounded-lg border border-orange-200 bg-white/70 p-3 shadow-[0_1px_3px_rgba(15,23,42,0.08)]">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="inline-flex items-center justify-center rounded-full bg-orange-100 text-orange-700 text-[10px] px-2 py-0.5">
                                    재접수 { (item.resubmissions || []).length }건
                                  </span>
                                  <span className="text-[11px] text-gray-500">
                                    가장 최근 요청:{" "}
                                    {item.resubmissions?.[item.resubmissions.length - 1]
                                      ?.requestedAt
                                      ? item.resubmissions[
                                          item.resubmissions.length - 1
                                        ].requestedAt
                                          .replace("T", " ")
                                          .slice(0, 16)
                                      : ""}
                                  </span>
                                </div>
                              </div>
                              <div className="space-y-2 max-h-48 overflow-y-auto pr-1 text-[11px] text-gray-700">
                                {(item.resubmissions || []).map((r, index) => (
                                  <div
                                    key={r.id}
                                    className="rounded-md border border-orange-100 bg-orange-50/70 px-3 py-2"
                                  >
                                    <div className="flex justify-between mb-1">
                                      <div className="flex items-center gap-2">
                                        <span className="inline-flex items-center justify-center rounded-full bg-white/80 text-orange-700 text-[10px] px-2 py-0.5">
                                          요청 {index + 1}
                                        </span>
                                        <span className="font-medium">
                                          {r.requestedBy?.name ?? "요청자"}
                                        </span>
                                      </div>
                                      <span className="text-[10px] text-gray-500">
                                        {r.requestedAt
                                          ? r.requestedAt
                                              .replace("T", " ")
                                              .slice(0, 16)
                                          : ""}
                                      </span>
                                    </div>
                                    <p className="whitespace-pre-wrap break-words leading-relaxed">
                                      {r.message}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </CompanyAdminLayout>
    </>
  );
};

export default ResubmissionManagement;
